import React from 'react';
import { fmtInches } from './InchesInput';
import { doorLayout, grilleLines } from './doorGeometry';

const TEAL = '#0D9488', INK = '#475569', GOLD = '#B8902E', ETCH = '#94A3B8';
const GLASS = 'rgba(13,148,136,0.16)', SLAB = '#FBFCFD', PANEL = '#EAF1F3';

// Puxador: alavanca · maçaneta · barra (pull) · conjunto c/ trinco (handleset). side = lado da folha onde está (a alavanca aponta para o meio)
const Handle = ({ hx, hy, style = 'lever', side = 'right', s, compact }) => {
  const k = compact ? 0.45 : 1;
  const dir = side === 'left' ? 1 : -1;
  if (style === 'knob') return <circle cx={hx} cy={hy} r={Math.max(1.5, 1.8 * s) * k} fill={INK} data-handle="knob" />;
  if (style === 'pull') { const L = Math.max(6, 10 * s) * k; return <rect x={hx - 1.2 * k} y={hy - L} width={2.4 * k} height={2 * L} rx={1} fill={INK} data-handle="pull" />; }
  if (style === 'gripset') { const L = Math.max(5, 8 * s) * k; return <g data-handle="gripset"><rect x={hx - 1.2 * k} y={hy - L * 0.6} width={2.4 * k} height={L * 1.6} rx={1} fill={INK} /><circle cx={hx} cy={hy - L * 0.95} r={Math.max(1.2, 1.5 * s) * k} fill={INK} /></g>; }
  return <g data-handle="lever"><circle cx={hx} cy={hy} r={Math.max(1.2, 1.3 * s) * k} fill={INK} /><line x1={hx} y1={hy} x2={hx + dir * Math.max(4, 5 * s) * k} y2={hy} stroke={INK} strokeWidth={1.8 * k} strokeLinecap="round" /></g>;
};

// Diagrama da porta (Entry/Patio) com vidro, painéis, grade e cotas. compact = miniatura sem cotas.
// opening = {type, width, height, door}; style/grille = config efetiva; panels = nº de painéis (patio)
export const DoorDiagram = ({ opening, style, grille = 'none', panels, width = 300, height = 190, compact = false, className = '', testId }) => {
  const g = doorLayout({ type: opening?.type, width: opening?.width, height: opening?.height, door: opening?.door, style, panels });
  if (!g) return null;
  const padL = compact ? 3 : 34, padR = compact ? 3 : 30, padB = compact ? 3 : 22, padT = compact ? 3 : 20;
  const s = Math.min((width - padL - padR) / g.w, (height - padT - padB) / g.h);
  const ox = padL + ((width - padL - padR) - g.w * s) / 2;
  const X = (x) => +(ox + x * s).toFixed(1);
  const Y = (y) => +(height - padB - y * s).toFixed(1);
  const R = (r, fill, sw, stroke = TEAL, key) => <rect key={key} x={X(r.x)} y={Y(r.y + r.h)} width={+(r.w * s).toFixed(1)} height={+(r.h * s).toFixed(1)} fill={fill} stroke={stroke} strokeWidth={sw} />;
  const glass = (r, key) => (
    <g key={key}>
      {R(r, GLASS, compact ? 0.6 : 1)}
      {grilleLines(r, grille).map((l, i) => <line key={i} x1={X(l[0])} y1={Y(l[1])} x2={X(l[2])} y2={Y(l[3])} stroke={l[4] === 'brass' ? GOLD : l[4] === 'etch' ? ETCH : INK} strokeWidth={compact ? 0.5 : l[4] === 'etch' ? 0.8 : 1} />)}
    </g>
  );
  const label = (v) => `${fmtInches(v)}"`;
  const tick = (x, y, horizontal) => (horizontal ? <line x1={x} y1={y - 3} x2={x} y2={y + 3} stroke={INK} strokeWidth="1" /> : <line x1={x - 3} y1={y} x2={x + 3} y2={y} stroke={INK} strokeWidth="1" />);
  const dims = compact ? [] : g.dims.filter((d) => d.value > 0);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className={className} data-testid={testId} role="img" data-grille={grille} data-style={style || ''}>
      {g.slabs.map((sl, i) => (
        <g key={`s${i}`} data-testid={testId ? `${testId}-slab-${i}` : undefined}>
          {R(sl, SLAB, compact ? 1 : 1.5)}
          {sl.panels.map((p, j) => R(p, PANEL, compact ? 0.5 : 1, TEAL, `p${j}`))}
          {sl.glass.map((gl, j) => glass(gl, `g${j}`))}
          {sl.handle && <Handle hx={X(sl.handle.x)} hy={Y(sl.handle.y)} style={sl.handle.style} side={sl.handle.side} s={s} compact={compact} />}
          {!compact && (sl.hinges || []).map((hp, j) => <rect key={`h${j}`} x={X(hp.x) - 2} y={Y(hp.y) - 3} width="4" height="6" fill="#fff" stroke={INK} strokeWidth="0.8" data-testid={testId && j === 0 ? `${testId}-hinge-${i}` : undefined} />)}
        </g>
      ))}
      {g.lites.map((lt, i) => (
        <g key={`l${i}`} data-testid={testId ? `${testId}-${lt.kind}-${i}` : undefined}>
          {R(lt, SLAB, compact ? 0.8 : 1.2)}
          {glass(lt.glass, 'g')}
        </g>
      ))}
      <rect x={X(0)} y={Y(g.h)} width={+(g.w * s).toFixed(1)} height={+(g.h * s).toFixed(1)} fill="none" stroke={TEAL} strokeWidth={compact ? 1.5 : 2} />
      {dims.map((d, i) => {
        const x1 = X(d.x1), y1 = Y(d.y1), x2 = X(d.x2), y2 = Y(d.y2);
        const main = d.side === 'bottom' || d.side === 'left';
        if (d.side === 'bottom' || d.side === 'top') {
          const y = d.side === 'bottom' ? y1 + 10 : y1 - 8;
          return <g key={i} data-testid={testId ? `${testId}-dim-${d.side}-${i}` : undefined}><line x1={x1} y1={y} x2={x2} y2={y} stroke={INK} strokeWidth="1" />{tick(x1, y, true)}{tick(x2, y, true)}<text x={(x1 + x2) / 2} y={d.side === 'bottom' ? y + 11 : y - 3} fontSize={main ? 10 : 9} fontWeight={main ? 700 : 500} fill={INK} textAnchor="middle" fontFamily="ui-monospace, monospace">{label(d.value)}</text></g>;
        }
        const x = d.side === 'left' ? x1 - 10 : x1 + 10;
        return <g key={i} data-testid={testId ? `${testId}-dim-${d.side}-${i}` : undefined}><line x1={x} y1={y1} x2={x} y2={y2} stroke={INK} strokeWidth="1" />{tick(x, y1, false)}{tick(x, y2, false)}<text x={d.side === 'left' ? x - 4 : x + 4} y={(y1 + y2) / 2 + 3} fontSize={main ? 10 : 9} fontWeight={main ? 700 : 500} fill={INK} textAnchor={d.side === 'left' ? 'end' : 'start'} fontFamily="ui-monospace, monospace">{label(d.value)}</text></g>;
      })}
    </svg>
  );
};
